import {Component, computed, effect, inject, input, output, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {FormControl} from '@angular/forms';
import {debounceTime} from 'rxjs';
import {TranslatePipe} from '@ngx-translate/core';
import {TodoStoreService} from '../../services/todo-store-service';
import {TodoQueryService} from '../../services/todo-query.service';
import {TodoCommandService} from '../../services/todo-command.service';
import {TodoPickerItem} from './todo-picker-item/todo-picker-item';
import {TodoCompletion, TodoSummary} from '../../../../../api';
import {Search} from '../../../../components/search/search';
import {ElciaBadge} from '../../../../components/badge/badge';

@Component({
  selector: 'elcia-todo-picker',
  imports: [
    TodoPickerItem,
    Search,
    ElciaBadge,
    TranslatePipe
  ],
  providers: [TodoStoreService, TodoQueryService, TodoCommandService],
  templateUrl: './todo-picker.html',
  styleUrl: './todo-picker.scss',
})
export class TodoPicker {
  onTodoSelect = output<TodoSummary | null>();
  control = input.required<FormControl<number | null>>();
  todoStore = inject(TodoStoreService);
  todos = computed(() => this.todoStore.todos());

  protected readonly completionOptions = [TodoCompletion.PENDING, TodoCompletion.DONE];
  protected readonly searchControl = new FormControl('', {nonNullable: true});
  private readonly selectedCompletions = signal<TodoCompletion[]>([TodoCompletion.PENDING]);

  private readonly searchText = toSignal(
    this.searchControl.valueChanges.pipe(debounceTime(500)),
    {initialValue: this.searchControl.value},
  );

  // The picker provides its own TodoQueryService, so these filters never touch the kanban ones.
  private readonly applyFilters = effect(() => this.setFilterParams());

  constructor() {
    // Applied synchronously so the very first fetch already carries the filter.
    this.setFilterParams();
  }

  protected isCompletionSelected(completion: TodoCompletion): boolean {
    return this.selectedCompletions().includes(completion);
  }

  protected toggleCompletion(completion: TodoCompletion): void {
    this.selectedCompletions.update(current => {
      if (!current.includes(completion)) return [...current, completion];
      // Keep at least one badge on: an empty list drops the filter and would show every todo.
      return current.length === 1 ? current : current.filter(c => c !== completion);
    });
  }

  private setFilterParams(): void {
    this.todoStore.filterParams.update(params => ({
      ...params,
      completions: this.selectedCompletions(),
      searchText: this.searchText(),
    }));
  }
}
